import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useInfiniteQuery } from '@/hooks/api/useInfiniteQuery';
import { AxiosResponse } from 'axios';
import { AssetDependency, SortOrder } from '@/api/generatedApi';
import { DependencyFilter, DependencySortField } from '@/api/dependencyTypes';
import { useMemo } from 'react';

export const GET_ASSET_DEPENDENCIES_QUERY_KEY = 'assets.getAssetDependencies';

interface GetAssetDependenciesResponse {
  dependencies: AssetDependency[];
  excludedResolveTargetIds?: string[];
  pageData: {
    total: number;
    currentPage: number;
    perPage: number;
    lastPage: number;
  };
}

export const useGetAssetDependencies = ({
  assetId,
  assetTenantId,
  enabled = true,
  refetchInterval,
  includeExcludedResolveTargets = false,
  filter,
  sortBy,
  sortOrder,
}: {
  assetId?: string;
  assetTenantId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
  includeExcludedResolveTargets?: boolean;
  filter?: DependencyFilter;
  sortBy?: DependencySortField;
  sortOrder?: SortOrder;
}) => {
  const { getTenantId } = useTenants();
  const tenantId = assetTenantId ?? getTenantId();

  const result = useInfiniteQuery<{ page?: number; perPage?: number }, GetAssetDependenciesResponse, 'dependencies'>({
    queryKey: [
      GET_ASSET_DEPENDENCIES_QUERY_KEY,
      { assetId, tenantId, includeExcludedResolveTargets, filter, sortBy, sortOrder },
    ],
    queryFunction: ({
      page,
      perPage,
    }: {
      page?: number;
      perPage?: number;
    }): Promise<AxiosResponse<GetAssetDependenciesResponse>> =>
      api.assets.getAssetDependenciesById({
        tenantId: tenantId,
        assetId: assetId ?? '',
        page: page ?? 1,
        perPage: perPage!,
        includeExcludedResolveTargets,
        filter,
        sortBy,
        sortOrder,
      }),
    queryParams: {},
    dataKey: 'dependencies',
    options: {
      enabled: enabled && !!assetId && !!tenantId,
      refetchInterval,
    },
  });

  const uniqueDependencies = useMemo(() => {
    const seen = new Set<string>();
    return (result.dependencies as AssetDependency[]).filter(dep => !seen.has(dep.path) && seen.add(dep.path));
  }, [result.dependencies]);

  const firstPageData = result.rawResult.data?.pages[0]?.data;
  const excludedResolveTargetIds = useMemo(() => {
    const ids = firstPageData?.excludedResolveTargetIds;
    return ids ? new Set(ids) : new Set<string>();
  }, [firstPageData?.excludedResolveTargetIds]);

  return {
    dependencies: uniqueDependencies,
    excludedResolveTargetIds,
    pageData: result.rawResult.data?.pages[0]?.data.pageData,
    isLoading: result.isLoading,
    error: result.error,
    refetch: () => result.rawResult.refetch(),
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
  };
};
